import {
  faFileAlt,
  faPencilAlt,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { B2Button } from "@hdb/biome-2";
import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserInfo } from "src/core/auth/auth.service";
import { RoleId } from "src/core/enums/userlist-enums";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";
import { getAllUnAssignedSite } from "src/store/slices/location-site.slice";
import { getRoles } from "src/store/slices/role.slice";
import { getSLS } from "src/store/slices/sls.slice";
import { getTownCouncils } from "src/store/slices/town-council.slice";
import * as yup from "yup";
import { string } from "yup";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import HasPermission from "../../../../../core/guards/has-permission";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../../shared/form-builder/form-builder.component";
import {
  addUser,
  deleteUser,
  editUser,
  getUserById,
  getUsers,
  resetUserDetails,
} from "../../../../../store/slices/user.slice";
import "../../../admin.scss";
import SearchBarComponent from "../../search-bar/search-bar.component";

interface UserListTableComponentProps {
  columns: any;
  data: any;
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: () => void;
  isLoading: boolean;
  onSearch: (val: string) => void;
}

const UserListTableComponent: FC<UserListTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
  onSearch,
}) => {
  const formRef = createRef();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const [isDetails, setIsDetails] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [roleOptions, setRoleOptions] = useState<any>([]);
  const [allRoleOptions, setAllRoleOptions] = useState<any>([]);
  const [townCouncilOptions, setTownCouncilOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [slsOptions, setSlsOptions] = useState<any>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<any>(null);
  const [selectedTcCode, setSelectedTcCode] = useState<any>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<any>(null);
  const [isSiteChanged, setIsSiteChanged] = useState<boolean>(false);
  const [initialValueSetCount, setInitialValueSetCount] = useState<any>(0);
  const [selectedIdToDeleteOrEdit, setSelectedIdToDeleteOrEdit] =
    useState<number>(0);

  const userInfo = getUserInfo();
  // let userInfo = getUserInfo();
  // userInfo.roleid = 9;
  const currentEnvironment = process.env.REACT_APP_CURRENT_ENVIRONMENT;

  const [initialValues, setInitialValues] = useState<any>({
    userId: "",
    email: "",
    profileName: "",
    // secretCode: "",
    roleId: "",
    supplierId: "",
    tcCode: "",
    siteCode: "",
  });

  const dispatch = useDispatch();
  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const isLoadingSiteByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const unassignedSites = useSelector((state: any) => {
    return state.locationSite.unassigneSites;
  });

  const userDetails = useSelector((state: any) => {
    return state.user.userDetails;
  });

  const isLoadingUserDetails = useSelector((state: any) => {
    return state.user.isLoadingUser;
  });

  const rolesResponse = useSelector((state: any) => {
    return state.role.roles;
  });

  const townCouncilsResponse = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const slsDataResponse = useSelector((state: any) => {
    return state.sls.sls;
  });

  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
    dispatch(getRoles());
    dispatch(getSLS());
  }, []);

  useEffect(() => {
    const roles = rolesResponse?.data?.map((role: any) => ({
      value: role?.id,
      label: role?.name,
    }));

    if (userInfo?.roleid == RoleId.TC_ADMIN) {
      const filteredRoles = roles?.filter(
        (role: any) => role?.value == RoleId.TC_OPERATOR
      );
      setAllRoleOptions(filteredRoles);
      setRoleOptions(filteredRoles);
    } else {
      setAllRoleOptions(roles);
      setRoleOptions(roles);
    }
  }, [rolesResponse]);

  function hasViewPermission(permissionByRolesId: number): boolean {
    for (const key in accessMatrix) {
      const entry = accessMatrix[key];
      permissionByRolesId =
        typeof permissionByRolesId === "number"
          ? permissionByRolesId
          : Number(permissionByRolesId);
      const hasViewAction = entry?.action?.includes("view");

      if (
        hasViewAction &&
        entry?.permissionByRoles?.includes(permissionByRolesId)
      ) {
        return true;
      }
    }

    return false;
  }

  const getPermissionOnRoles = (permissionByRolesId: number): number[] => {
    const result = new Set<number>();

    for (const key in accessMatrix) {
      const entry = accessMatrix[key];
      permissionByRolesId =
        typeof permissionByRolesId === "number"
          ? permissionByRolesId
          : Number(permissionByRolesId);
      if (
        entry?.permissionByRoles?.includes(permissionByRolesId) &&
        entry?.action?.includes("add")
      ) {
        entry?.permissionOnRoles?.forEach((roleId) => result.add(roleId));
      }
    }

    return Array.from(result);
  };

  useEffect(() => {
    if (
      !userInfo ||
      Object.keys(userInfo).length === 0 ||
      roleOptions?.length == 0 ||
      (!isAdd && !isEdit)
    ) {
      return;
    }
    const userRoles = getPermissionOnRoles(userInfo?.roleid);
    const roles = rolesResponse?.data
      ?.filter((role: any) => userRoles.includes(role?.id))
      ?.map((role: any) => ({
        value: role?.id,
        label: role?.name,
      }));

    const allRoles = rolesResponse?.data?.map((role: any) => ({
      value: role?.id,
      label: role?.name,
    }));

    setAllRoleOptions(allRoles);

    setRoleOptions(roles);

    // if (userInfo?.roleid == RoleId.TC_ADMIN) {
    //   const filteredRoles = roles?.filter(
    //     (role: any) => role?.value == RoleId.TC_OPERATOR
    //   );

    //   setRoleOptions(filteredRoles);
    // } else {
    //   setRoleOptions(roles);
    // }
  }, [isAdd, isEdit, userDetails]);

  useEffect(() => {
    const townCouncils = townCouncilsResponse?.data?.map((tc: any) => ({
      value: tc?.tcCode,
      label: tc?.name,
    }));

    setTownCouncilOptions(townCouncils);
  }, [townCouncilsResponse]);

  useEffect(() => {
    const sites = unassignedSites?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    setSiteOptions(sites);
  }, [unassignedSites]);

  useEffect(() => {
    const slsData = slsDataResponse?.data?.map((sls: any) => ({
      value: sls?.id,
      label: sls?.name,
    }));

    setSlsOptions(slsData);
  }, [slsDataResponse]);

  useEffect(() => {
    if (!selectedIdToDeleteOrEdit) return;
    if (isEdit || isDetails) {
      dispatch(getUserById({ id: selectedIdToDeleteOrEdit }));
    }
  }, [isEdit, isDetails]);

  useEffect(() => {
    if (!userDetails) return;

    setInitialValues({
      userId: userDetails?.userId,
      email: userDetails?.email,
      profileName: userDetails?.profileName,
      // secretCode: userDetails?.secretCode,
      roleId: userDetails?.roleId,
      tcCode: userDetails?.tcCode,
      siteCode: [],
      supplierId: userDetails?.supplierId || "",
    });

    setSelectedSupplierId(userDetails?.supplierId || null);
    setSelectedTcCode(userDetails?.tcCode);

    if (userDetails?.siteList) {
      const siteOptions = userDetails?.siteList?.map((site: any) => ({
        label: site?.siteName,
        value: site?.siteCode,
      }));

      setSiteOptions(siteOptions);
    }
  }, [userDetails]);

  useEffect(() => {
    const siteCodes: any[] = [];

    const form: any = formRef.current;

    if (form) {
      const copiedInitialValues = structuredClone(initialValues);

      if (form.values.siteCode?.length) {
        siteCodes.push(...form.values.siteCode);
      }
      else if (!isSiteChanged && userDetails?.sites?.length && siteOptions?.length) {
        for (let site of userDetails?.sites || []) {
          if (siteOptions.some((option: any) => option.value === site)) {
            siteCodes.push(
              siteOptions.find((option: any) => option.value === site)
            );
          }
        }
      }

      let data = {
        ...copiedInitialValues,
        siteCode: siteCodes
      };

      if (
        form.values.userId &&
        Number(userDetails?.userId) !== Number(form.values.userId)
      ) {
        data.userId = form.values.userId;
      }
      if (
        form.values.email &&
        Number(userDetails?.email) !== Number(form.values.email)
      ) {
        data.email = form.values.email;
      }
      if (
        form.values.profileName &&
        Number(userDetails?.profileName) !== Number(form.values.profileName)
      ) {
        data.profileName = form.values.profileName;
      }
      // if (
      //   form.values.secretCode &&
      //   Number(userDetails?.secretCode) !== Number(form.values.secretCode)
      // ) {
      //   data.secretCode = form.values.secretCode;
      // }

      if (
        form.values.roleId &&
        Number(userDetails?.roleId) !== Number(form.values.roleId)
      ) {
        data.roleId = form.values.roleId;
      }

      if (
        form.values.supplierId &&
        Number(userDetails?.supplierId) !== Number(form.values.supplierId)
      ) {
        data.supplierId = form.values.supplierId;
      }

      if (
        form.values.tcCode &&
        Number(userDetails?.tcCode) !== Number(form.values.tcCode)
      ) {
        data.tcCode = form.values.tcCode;
      }

      setInitialValues(data);
    } else {
      const copiedInitialValues = structuredClone(initialValues);

      for (let site of siteCodes) {
        if (
          !copiedInitialValues.siteCode.some((b: any) => b.value === site.value)
        ) {
          copiedInitialValues.siteCode.push(site);
        }
      }

      setInitialValues(copiedInitialValues);
    }
  }, [siteOptions]);

  const [formConfiguration, setFormConfiguration] = useState<FormConfig>({
    controls: [],
  });

  useEffect(() => {
    setInitialValueSetCount(initialValueSetCount + 1);
  }, [initialValues]);

  const onChangeRole = (value: any) => {
    setSelectedRoleId(value);

    if (selectedIdToDeleteOrEdit) {
      const copiedInitialValues = structuredClone(initialValues);
      copiedInitialValues.tcCode = "";
      copiedInitialValues.roleId = value;
      setInitialValues(copiedInitialValues);
    }
  };

  const onChangeSupplier = (selectedSupplierId: string) => {
    if (!selectedSupplierId) return;

    setSelectedSupplierId(selectedSupplierId);
  };

  const onChangeTC = (selectedTC: any) => {
    if (!selectedTC) return;

    setSelectedTcCode(selectedTC);
  };

  const onChangeSite = (selected: any) => {
    // If lengths differ -> changed. If lengths equal -> compare sets.
    const selectedValues = (selected || []).map((s: any) => s?.value).sort();
    const siteValues = (siteOptions || []).map((s: any) => s?.value).sort();

    const isSameLength = selectedValues.length === siteValues.length;
    const isSameSet = isSameLength && selectedValues.every((v: any, i: number) => v === siteValues[i]);

    const isChanged = !isSameSet;
    setIsSiteChanged(isChanged);
  };

  useEffect(() => {
    setSiteOptions([]);
  }, []);


  useEffect(() => {
    if (selectedSupplierId && selectedTcCode) {
      dispatch(
        getAllUnAssignedSite({
          tcCode: selectedTcCode,
          supplierId: selectedSupplierId,
        })
      );
    }
  }, [selectedSupplierId, selectedTcCode]);

  useEffect(() => {
    setFormConfiguration({ controls: [] });
    const roleId = selectedRoleId ?? userDetails?.roleId;
    const isHDBAdmin = Number(roleId) === RoleId.HDB_ADMIN;

    const isCurrentUser =
      Number(
        userInfo[
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
        ]
      ) == Number(userDetails?.id);

    let roleOptionsForForm = roleOptions;
    if (isDetails || (isCurrentUser && isEdit)) {
      roleOptionsForForm = allRoleOptions;
    }

    const controls = [
      {
        name: "roleId",
        label: "Role",
        placeholder: "Select Role",
        type: "searchable-select",
        options: roleOptionsForForm,
        required: !isDetails,
        readOnly: isDetails || (isCurrentUser && isEdit),
        disabled: isDetails || (isCurrentUser && isEdit),
        onChange: onChangeRole,
      },
      {
        name: "email",
        label: "Email",
        placeholder: "example@domain.com",
        type: "email",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
        // info: "For WOG ADFS Users",
        display:
          currentEnvironment == "sit"
            ? !isDetails && !isEdit
            : !isDetails && !isEdit && isHDBAdmin,
      },
      {
        name: "userId",
        label: "User ID",
        placeholder: isHDBAdmin
          ? "HDB12345"
          : "<UEN><NRIC> (e.g XXXXXXXXXYYYYYYYYY)",
        type: "text",
        required: !isDetails,
        readOnly: true,
        disabled: isDetails,
        // info: "For CorpPass Users",
        display: currentEnvironment == "sit" ? false : !isDetails && !isEdit,
      },
      {
        name: "profileName",
        label: "User Name",
        placeholder: "User Name",
        type: "text",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      // {
      //   name: "secretCode",
      //   label: "Secret Code",
      //   placeholder: isDetails || isEdit ? "******" : "Secret Code",
      //   type: isDetails || isEdit ? "password" : "number",
      //   required: true,
      //   info: "Keep your secret code for later use. Can be modified later in profile",
      //   readOnly: isDetails,
      //   disabled: isDetails,
      // },
    ];

    let updatedControls: any = [];

    if (
      Number(roleId) === RoleId.HDB_ADMIN ||
      Number(roleId) === RoleId.HDB_VIEW ||
      Number(roleId) === RoleId.SLP_OPERATOR
    ) {
      updatedControls = [...controls];
    } else if (Number(roleId) === RoleId.SLS_SUPPLIER) {
      const newItems = [
        {
          name: "supplierId",
          label: "Supplier",
          placeholder: "Select Supplier",
          type: "searchable-select",
          options: slsOptions,
          onChange: onChangeSupplier,
          required: true,
          readOnly: isDetails,
          disabled: isDetails,
        },
        {
          name: "tcCode",
          label: "Town Council",
          placeholder: "Select Town Council",
          type: "searchable-select",
          options: townCouncilOptions,
          readOnly: isDetails,
          disabled: isDetails,
          onChange: onChangeTC,
          isLoading: isLoadingTownCouncils,
        },
        {
          name: "siteCode",
          label: "Site",
          placeholder: "Select Site",
          type: "multiselect",
          selectedValue: "WSII",
          options: siteOptions,
          onChange: onChangeSite,
          selectableValue: "siteCode",
          // required: true,
          readOnly: isDetails,
          disabled: isDetails,
          isLoading: isLoadingSiteByTCCode,
        },
      ];

      updatedControls = [...controls, ...newItems];
    } else if (
      Number(roleId) === RoleId.TC_ADMIN ||
      Number(roleId) === RoleId.TC_OPERATOR ||
      Number(roleId) === RoleId.AGENCY_ADMIN ||
      Number(roleId) === RoleId.AGENCY_OPERATOR ||
      Number(roleId) === RoleId.AGENCY_USER
    ) {
      const isCurrentUser =
        Number(
          userInfo[
          "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
          ]
        ) == Number(userDetails?.id);
      const newItems = [
        {
          name: "tcCode",
          label: "Town Council",
          placeholder: "Select Town Council",
          type: "searchable-select",
          options: townCouncilOptions,
          required: true,
          readOnly: isDetails,
          disabled: isDetails || (isCurrentUser && isEdit),
        },
      ];

      updatedControls = [...controls, ...newItems];
    }

    setFormConfiguration({
      controls: updatedControls.length ? updatedControls : controls,
    });
  }, [
    selectedRoleId,
    roleOptions,
    slsOptions,
    userDetails,
    siteOptions,
    isDetails,
  ]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setIsDetails(false);
    setIsDelete(false);
    setIsAdd(false);
    setSiteOptions([]);
    setFormConfiguration({ controls: [] });
    setSelectedRoleId(1);
    dispatch(resetUserDetails({}));
    setSelectedRoleId(null);
    setIsSiteChanged(false);
    setSelectedIdToDeleteOrEdit(0);
    setInitialValues({
      userId: "",
      email: "",
      profileName: "",
      // secretCode: "",
      roleId: "",
      tcCode: "",
      siteCode: "",
      supplierId: "",
    });

    const form: any = formRef.current;
    if (form) {
      form.resetForm();
    }
  };

  const detailsAction = (id: number) => {
    setSelectedRoleId(null);
    setSelectedIdToDeleteOrEdit(id);

    setIsDetails(true);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(false);
  };

  const editAction = (id: number) => {
    setSelectedRoleId(null);
    setSelectedIdToDeleteOrEdit(id);

    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(true);
    setIsDelete(false);
  };

  const deleteAction = (id: number) => {
    setSelectedRoleId(null);
    setSelectedIdToDeleteOrEdit(id);

    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(true);
  };

  const handleDelete = () => {
    dispatch(deleteUser({ id: selectedIdToDeleteOrEdit })).then(() => {
      onRefetchData();
      handleCloseModal();
    });
  };

  const handleAddUser = () => {
    setInitialValues({
      userId: "",
      email: "",
      profileName: "",
      // secretCode: "",
      roleId: "",
      tcCode: "",
      siteCode: "",
      supplierId: "",
    });

    setIsDetails(false);
    setIsModalOpen(true);
    setIsAdd(true);
    setIsEdit(false);
    setIsDelete(false);
  };

  const onSubmit = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      let sites = [];
      const isAdd = !isEdit && !isDetails;

      if (form?.values?.siteCode?.length) {
        sites = form.values.siteCode.map((site: any) => site.value);
      }

      const formValues = form.values;

      const payload = { ...formValues, sites, id: selectedIdToDeleteOrEdit };
      const { email, profileName, roleId, /*secretCode,*/ supplierId, userId } =
        payload;
      const role = Number(form?.values?.roleId);

      if (!profileName /*|| !secretCode*/ || !roleId) {
        return;
      }

      if (currentEnvironment == "sit") {
        if (
          (isAdd && !email && !userId) ||
          (isAdd &&
            (role === RoleId.HDB_ADMIN || role === RoleId.HDB_VIEW) &&
            (!email || !userId)) ||
          (email === "" && userId === "")
        ) {
          return;
        }
      } else {
        if (
          (isAdd && !email && !userId) ||
          (isAdd && role === RoleId.HDB_ADMIN && (!email || !userId)) ||
          (email === "" && userId === "")
        ) {
          return;
        }
      }

      // Check all the values validation
      if (
        profileName?.length < 2 ||
        profileName?.length > 50 ||
        email?.length > 50 ||
        userId?.length > 50
        // || secretCode?.length > 20
      ) {
        return;
      }

      if (role === RoleId.SLS_SUPPLIER && !supplierId) {
        return;
      }

      // if (role === RoleId.SLS_SUPPLIER && !sites.length) {
      //   return;
      // }

      if (
        role === RoleId.HDB_ADMIN ||
        role === RoleId.HDB_VIEW ||
        role === RoleId.SLP_OPERATOR
      ) {
        //HDB Master admin won't have tc, block, suplier.
        delete payload.tcCode;
        delete payload.supplierId;
        delete payload.siteCode;
        delete payload.sites;
      } else if (
        role === RoleId.TC_ADMIN ||
        role === RoleId.TC_OPERATOR ||
        role === RoleId.AGENCY_ADMIN ||
        role === RoleId.AGENCY_OPERATOR ||
        role === RoleId.AGENCY_USER
      ) {
        // for TC Admin only TC will be there rest are deleted

        delete payload.supplierId;
        delete payload.siteCode;
        delete payload.sites;
      } else if (role === RoleId.SLS_SUPPLIER) {
        // for Supplier only blocks and suppllier id will be there
        delete payload.tcCode;
      }

      if (isEdit) {
        payload.email = null;
        payload.userId = null;

        dispatch(
          editUser({ payload: payload, id: selectedIdToDeleteOrEdit })
        ).then(() => {
          handleCloseModal();
          onRefetchData();
        });
      } else {
        // Add User...
        let payloadItems = {};

        if (
          role === RoleId.HDB_ADMIN ||
          role === RoleId.HDB_VIEW ||
          role === RoleId.SLP_OPERATOR
        ) {
          let { supplierId, tcCode, siteCode, ...updatedFormValues } =
            formValues || {};
          payloadItems = { ...updatedFormValues, sites };
        } else if (role === RoleId.TC_ADMIN || role === RoleId.TC_OPERATOR) {
          let { supplierId, siteCode, ...updatedFormValues } = formValues || {};
          payloadItems = { ...updatedFormValues, sites };
        } else if (role === RoleId.SLS_SUPPLIER) {
          let { tcCode, siteCode, ...updatedFormValues } = formValues;
          payloadItems = { ...updatedFormValues, sites };
        } else {
          let { supplierId, siteCode, ...updatedFormValues } = formValues || {};
          payloadItems = { ...updatedFormValues, sites };
        }

        dispatch(addUser({ payload: payloadItems })).then(() => {
          handleCloseModal();
          onRefetchData();
        });
      }
    }
  };

  let modalActionConfig: any = [];

  if (isDelete) {
    modalActionConfig = [
      {
        label: "Yes",
        event: handleDelete,
        variant: "primary",
      },
    ];
  } else if (!isDetails) {
    modalActionConfig = [
      {
        label: "Save",
        event: onSubmit,
        variant: "primary",
      },
    ];
  }

  const actionConfig: any = [
    {
      label: "Edit",
      actionProperty: "id",
      event: editAction,
      variant: "secondary",
      className: "mx-2 my-1",
      moduleName: "Users",
      actionName: "Edit-Users",
      icon: faPencilAlt,
      isDisabled: isDisabledButton,
    },
    {
      label: "View",
      actionProperty: "id",
      event: detailsAction,
      variant: "info",
      className: "mx-2 my-1",
      moduleName: "Users",
      actionName: "View-Users",
      icon: faFileAlt,
      isDisabled: isDisabledButton,
    },
    {
      label: "Delete",
      actionProperty: "id",
      event: deleteAction,
      variant: "danger",
      className: "mx-2 my-1",
      moduleName: "Users",
      actionName: "Delete-Users",
      icon: faTrashAlt,
      isDisabled: isDisabledButton,
    },
  ];

  type AccessEntry = {
    action: string[];
    permissionOnRoles: number[];
    permissionByRoles: number[];
  };

  const accessMatrix: { [key: string]: AccessEntry } = {
    TA05000701: {
      action: ["view", "details"],
      permissionOnRoles: [1, 2, 3, 4, 5, 6, 7, 8, 9],
      permissionByRoles: [1, 2, 4, 8, 9],
    },
    TA05000702: {
      action: ["add", "edit", "delete"],
      permissionOnRoles: [1, 2, 4], // role on which action is performed
      permissionByRoles: [1, 9], // role performing the action
    },
    TA05000703: {
      action: ["add", "edit", "delete"],
      permissionOnRoles: [3, 7],
      permissionByRoles: [1, 2],
    },
    TA05000704: {
      action: ["add", "edit", "delete"],
      permissionOnRoles: [5, 6],
      permissionByRoles: [1, 5, 9],
    },
    // This permission is not in the access matrix. It was created based on Nayan Vai's comment (TA05000705 - the function name is also assumed).
    TA05000705: {
      action: ["add", "edit", "delete"],
      permissionOnRoles: [8, 9],
      permissionByRoles: [1],
    },
    TA05000706: {
      action: ["add", "edit", "delete"],
      permissionOnRoles: [9], // removed 7
      permissionByRoles: [9],
    },
    // TA05000705: {
    //   action: ["approve roles", "reject roles"],
    //   permissionOnRoles: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    //   permissionByRoles: [1],
    // },
    // TA05000706: {
    //   action: ["view roles"],
    //   permissionOnRoles: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    //   permissionByRoles: [1, 8, 9],
    // },
  };

  /**
   * Check permission based on roles and action
   * @param byRoleId - ID of the role performing the action
   * @param onRoleId - ID of the role on which action is performed
   * @param action - Action name
   * @returns true if permission exists, false otherwise
   */
  function hasPermission(
    byRoleId: number,
    onRoleId: number,
    action: string
  ): boolean {
    for (const key in accessMatrix) {
      const entry = accessMatrix[key];
      byRoleId = typeof byRoleId === "number" ? byRoleId : Number(byRoleId);
      onRoleId = typeof onRoleId === "number" ? onRoleId : Number(onRoleId);

      if (
        entry?.action?.includes(action.toLowerCase()) &&
        entry?.permissionByRoles?.includes(byRoleId) &&
        entry?.permissionOnRoles?.includes(onRoleId)
      ) {
        return true;
      }
    }
    return false;
  }

  function isDisabledButton(data: any, label: any): boolean {
    // console.log(userInfo?.email,userInfo?.userId,data?.email, label)
    const isCurrentUser =
      Number(
        userInfo[
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
        ]
      ) == Number(data?.id);

    if (isCurrentUser && label == "Edit") {
      return true;
    }

    if (isCurrentUser && label == "Details") {
      return true;
    }

    if (isCurrentUser && label == "Delete") {
      return false;
    }

    const result = hasPermission(userInfo?.roleid, data?.roleId, label);
    return result;
  }

  const emptyConfig = {
    title: "No User Found",
    description: "",
  };

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email("Please enter a valid email address")
      .max(50, "Email should not exceed 50 characters")
      //.required("Email is required"),
      .test("email-required", "Email is required", function (value) {
        return !!value;
      }),
    userId: yup
      .string()
      .max(50, "User ID should not exceed 50 characters")
      .test("email-or-userId", "User ID is required", function (value) {
        return !!value;
      }),
    profileName: yup
      .string()
      .min(2, "Must be at least 2 characters")
      .max(50, "should not exceed 50 characters")
      .required("User Name is required"),
    // secretCode: yup
    //   .string()
    //   .matches(/^\d{0,20}$/, "Secret Code must be up to 20 digits")
    //   .required("Secret Code is required"),
    roleId: yup.string().required("Role is required"),
    tcCode: yup.string().when("roleId", {
      is: (val: string) => {
        return ["2", "3", "4", "5", "6"].includes(val);
      },
      then: (schema: any) => schema.required("Town Council is required"),
      otherwise: (schema: any) => schema.notRequired(), // Explicitly make optional
    }),
    supplierId: string().when("roleId", {
      is: "7",
      then: (schema: any) => schema.required("Supplier is required"),
    }),
    siteCode: yup.array().when("roleId", {
      is: "7",
      then: (schema: any) => schema.notRequired(),
    }),
  });

  useEffect(() => {
    const form: any = formRef.current;
    if (form && selectedRoleId === "7") {
      form.setFieldError("tcCode", undefined); // Clear any existing error
    }
  }, [selectedRoleId]);

  const userTableData = userInfo?.roleid
    ? hasViewPermission(userInfo?.roleid)
      ? data
      : []
    : [];

  let modalHeaderTitle = "Add User";

  if (isEdit) {
    modalHeaderTitle = "Edit User";
  } else if (isDetails) {
    modalHeaderTitle = "User Details";
  } else if (isDelete) {
    modalHeaderTitle = "Delete User";
  }

  let modalBodyContent = null;

  if (isDelete) {
    modalBodyContent = (
      <h5 className="text-center">
        Are you sure to <b className="text-danger">Delete</b>?
      </h5>
    );
  } else if (isAdd || isEdit || isDetails) {
    modalBodyContent = (
      <SkeletonComponent
        wrapperHeight="380px"
        skeletonHeight="50px"
        isLoading={isEdit || isDetails ? isLoadingUserDetails : false}
        count={6}
      >
        <FormBuilderComponent
          renderCount={initialValueSetCount}
          formRef={formRef}
          initialValues={initialValues}
          validation={validationSchema}
          formConfig={formConfiguration}
        />
      </SkeletonComponent>
    );
  }

  return (
    <div>
      <div>
        <div className="d-flex gap-3 justify-content-end table-toolbar">
          <SearchBarComponent
            placeholder="Search User Name"
            methodToDispatch={getUsers}
            getSearch={onSearch}
          />
          <HasPermission
            moduleName="Users"
            actionName="Add-Users"
            isRoute={false}
          >
            <B2Button onClick={handleAddUser} variant="primary">
              Add User
            </B2Button>
          </HasPermission>
        </div>

        <div className="my-3">
          <TableComponent
            tableHeaders={columns}
            emptyConfig={emptyConfig}
            data={userTableData}
            totalNumberOfRecords={totalNumberOfRecords}
            pageSize={pageSize}
            current={current}
            onChangePage={onChangePage}
            actionConfig={actionConfig}
            onChangeSort={onChangeSort}
            isLoadingData={isLoading}
          />
        </div>
      </div>

      {/* modals */}
      {isModalOpen && (
        <ModalComponent
          isConformationModal={isDelete}
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          headerTitle={modalHeaderTitle}
          actionConfig={modalActionConfig}
        >
          {modalBodyContent}
        </ModalComponent>
      )}
    </div>
  );
};

export default UserListTableComponent;
